import { ref, computed } from '@vue/composition-api'
import axios from '@axios'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import router from '@/router'

const BLANK_FILTER = () => ({
  keyword: '',
  propertyfilter: '',
  action_id: '',
  property_type: null,
  bedrooms: '',
  priceMin: null,
  priceMax: null,
  sqm_min: null,
  sqm_max: null,
  bts_ids: '',
  mrt_ids: '',
  arl_ids: '',
  zones: '',
  minDateExpire: null,
  maxDateExpire: null,
  minDateAvl: null,
  maxDateAvl: null,
  place_id: null,
  place_name: '',
  owner_id: null,
  owner_name: '',
  unit: '',
  floor: '',
  type_name: '',
  building: '',
  fac_ids: '',
  amenity_ids: '',
  furniture_ids: '',
  statusId: null,
  showSold: false, // Sold is left out of the list until "Sold" is ticked
})

// property_status.id of "Sold"
const SOLD_STATUS_ID = 4

function filterFromQuery(q) {
  const f = BLANK_FILTER()
  if (q.keyword)       f.keyword        = q.keyword
  if (q.propertyfilter) f.propertyfilter = q.propertyfilter
  if (q.action_id)     f.action_id      = q.action_id
  if (q.type_id)       f.property_type  = Number(q.type_id)
  if (q.bedrooms)      f.bedrooms       = q.bedrooms
  if (q.minPrice)      f.priceMin       = Number(q.minPrice)
  if (q.maxPrice)      f.priceMax       = Number(q.maxPrice)
  if (q.sqm_min)       f.sqm_min        = Number(q.sqm_min)
  if (q.sqm_max)       f.sqm_max        = Number(q.sqm_max)
  if (q.bts_ids)       f.bts_ids        = q.bts_ids
  if (q.mrt_ids)       f.mrt_ids        = q.mrt_ids
  if (q.arl_ids)       f.arl_ids        = q.arl_ids
  if (q.zones)         f.zones          = q.zones
  if (q.expire_start)  f.minDateExpire  = q.expire_start
  if (q.expire_end)    f.maxDateExpire  = q.expire_end
  if (q.avl_start)     f.minDateAvl     = q.avl_start
  if (q.avl_end)       f.maxDateAvl     = q.avl_end
  if (q.place_id)      f.place_id       = Number(q.place_id)
  if (q.place_name)    f.place_name     = q.place_name
  if (q.owner_id)      f.owner_id       = Number(q.owner_id)
  if (q.owner_name)    f.owner_name     = q.owner_name
  if (q.unit)          f.unit           = q.unit
  if (q.floor)         f.floor          = q.floor
  if (q.type_name)     f.type_name      = q.type_name
  if (q.building)      f.building       = q.building
  if (q.fac_ids)       f.fac_ids        = q.fac_ids
  if (q.amenity_ids)   f.amenity_ids    = q.amenity_ids
  if (q.furniture_ids) f.furniture_ids  = q.furniture_ids
  if (q.statusId)      f.statusId       = Number(q.statusId)
  if (q.show_sold === '1') f.showSold   = true
  return f
}

export default function usePropertyListV2() {
  const toast = useToast()

  const isLoading    = ref(false)
  const properties   = ref([])
  const total        = ref(0)
  const perPage      = ref(25)
  const currentPage  = ref(1)
  const sortOption   = ref('newest')
  const perPageOptions = [10, 25, 50, 100]

  const filter = ref(filterFromQuery(router.currentRoute.query))

  const dataMeta = computed(() => {
    const from = perPage.value * (currentPage.value - 1) + (properties.value.length ? 1 : 0)
    const to   = perPage.value * (currentPage.value - 1) + properties.value.length
    return { from, to, of: total.value }
  })

  const resolveSort = () => {
    const map = {
      sale_price_asc:  { sortBy: 'sale_price', sortDesc: false },
      sale_price_desc: { sortBy: 'sale_price', sortDesc: true  },
      rent_price_asc:  { sortBy: 'rent_price', sortDesc: false },
      rent_price_desc: { sortBy: 'rent_price', sortDesc: true  },
      bedroom_asc:     { sortBy: 'bedroom',    sortDesc: false },
      bedroom_desc:    { sortBy: 'bedroom',    sortDesc: true  },
      floor_asc:       { sortBy: 'floors',     sortDesc: false },
      floor_desc:      { sortBy: 'floors',     sortDesc: true  },
      sqm_asc:         { sortBy: 'sqm',        sortDesc: false },
      sqm_desc:        { sortBy: 'sqm',        sortDesc: true  },
    }
    return map[sortOption.value] || { sortBy: 'id', sortDesc: true }
  }

  const fetchProperties = async () => {
    isLoading.value = true
    const { sortBy, sortDesc } = resolveSort()
    const f = filter.value

    const payload = {
      keyword:        f.keyword,
      propertyfilter: f.propertyfilter,
      action_id:      f.action_id,
      type_id:        f.property_type,
      bedrooms:       f.bedrooms,
      minPrice:       f.priceMin,
      maxPrice:       f.priceMax,
      sqm_min:        f.sqm_min,
      sqm_max:        f.sqm_max,
      bts_ids:        f.bts_ids,
      mrt_ids:        f.mrt_ids,
      arl_ids:        f.arl_ids,
      zones:          f.zones,
      expire_start:   f.minDateExpire,
      expire_end:     f.maxDateExpire,
      avl_start:      f.minDateAvl,
      avl_end:        f.maxDateAvl,
      place_id:       f.place_id,
      owner_id:       f.owner_id,
      unit:           f.unit,
      floor:          f.floor,
      type_name:      f.type_name,
      building:       f.building,
      fac_ids:        f.fac_ids,
      amenity_ids:    f.amenity_ids,
      furniture_ids:  f.furniture_ids,
      statusId:       f.statusId,
      // hide Sold (status 4) unless "Sold" is ticked — or a status was picked explicitly
      ...(!f.showSold && !f.statusId ? { exclude_status_id: String(SOLD_STATUS_ID) } : {}),
      perpage:        perPage.value,
      offset:         perPage.value * (currentPage.value - 1),
      is_approve:     1,
      sortBy,
      sortDesc,
    }

    try {
      const res = await axios.post('/property/v2/filter', payload)
      const toDate = d => d ? new Date(d).toLocaleDateString('sv-SE') : null
      properties.value = (res.data.data || []).map(v => ({
        ...v,
        availble_date: toDate(v.availble_date),
        expire_date:   toDate(v.expire_date),
        _showDetails: false,
      }))
      total.value = res.data.total || 0
      syncQueryURL()
    } catch {
      toast({
        component: ToastificationContent,
        props: { title: 'Error fetching properties', icon: 'AlertTriangleIcon', variant: 'danger' },
      })
    } finally {
      isLoading.value = false
    }
  }

  const syncQueryURL = () => {
    const f = filter.value
    const q = {}
    if (f.keyword)        q.keyword        = f.keyword
    if (f.propertyfilter) q.propertyfilter = f.propertyfilter
    if (f.action_id)      q.action_id      = f.action_id
    if (f.property_type)  q.type_id        = f.property_type
    if (f.bedrooms)       q.bedrooms       = f.bedrooms
    if (f.priceMin)       q.minPrice       = f.priceMin
    if (f.priceMax)       q.maxPrice       = f.priceMax
    if (f.sqm_min)        q.sqm_min        = f.sqm_min
    if (f.sqm_max)        q.sqm_max        = f.sqm_max
    if (f.bts_ids)        q.bts_ids        = f.bts_ids
    if (f.mrt_ids)        q.mrt_ids        = f.mrt_ids
    if (f.arl_ids)        q.arl_ids        = f.arl_ids
    if (f.zones)          q.zones          = f.zones
    if (f.minDateExpire)  q.expire_start   = f.minDateExpire
    if (f.maxDateExpire)  q.expire_end     = f.maxDateExpire
    if (f.minDateAvl)     q.avl_start      = f.minDateAvl
    if (f.maxDateAvl)     q.avl_end        = f.maxDateAvl
    if (f.place_id)       q.place_id       = f.place_id
    if (f.place_name)     q.place_name     = f.place_name
    if (f.owner_id)       q.owner_id       = f.owner_id
    if (f.owner_name)     q.owner_name     = f.owner_name
    if (f.unit)           q.unit           = f.unit
    if (f.floor)          q.floor          = f.floor
    if (f.type_name)      q.type_name      = f.type_name
    if (f.building)       q.building       = f.building
    if (f.fac_ids)        q.fac_ids        = f.fac_ids
    if (f.amenity_ids)    q.amenity_ids    = f.amenity_ids
    if (f.furniture_ids)  q.furniture_ids  = f.furniture_ids
    if (f.statusId)       q.statusId       = f.statusId
    if (f.showSold)       q.show_sold      = 1

    const cur = router.currentRoute.query
    if (new URLSearchParams(q).toString() !== new URLSearchParams(cur).toString()) {
      router.replace({ query: q }).catch(() => {})
    }
  }

  const applyFilter = newFilter => {
    filter.value = { ...filter.value, ...newFilter }
    currentPage.value = 1
    fetchProperties()
  }

  const clearFilter = () => {
    filter.value = BLANK_FILTER()
    currentPage.value = 1
    fetchProperties()
  }

  const deleteProperty = async id => {
    try {
      await axios.delete(`/property/v1/delete/${id}`)
      toast({
        component: ToastificationContent,
        props: { title: 'Property deleted', icon: 'CheckIcon', variant: 'success' },
      })
      fetchProperties()
    } catch {
      toast({
        component: ToastificationContent,
        props: { title: 'Error deleting property', icon: 'AlertTriangleIcon', variant: 'danger' },
      })
    }
  }

  fetchProperties()

  return {
    isLoading, properties, total, perPage, currentPage, perPageOptions,
    sortOption, filter, dataMeta, fetchProperties, applyFilter, clearFilter, deleteProperty,
  }
}
